import { inject } from '@angular/core';

import {
  patchState,
  signalStore,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';

import { DbService } from '../db';
import { MainConfig } from '../db/models';
import {
  ActiveMainConfigModel,
  ModeModel,
  PlateformModel,
  ScreenStateModel,
  ThemeModel,
} from '../services/config/main-config.models';

interface MainConfigState {
  config: ActiveMainConfigModel;
  isHydrated: boolean;
}

const configInitialState: MainConfigState = {
  config: {
    activeMode: 'light',
    activePlateform: 'authentification',
    activeTheme: 'magis',
    screenLocked: 'unlocked',
  },
  isHydrated: false,
};

export const ConfigStore = signalStore(
  { providedIn: 'root' },
  withState(configInitialState),
  withMethods((store, dbService = inject(DbService)) => ({
    async hydrate() {
      if (store.isHydrated()) {
        return;
      }

      try {
        await firstValueFrom(dbService.dbIsReady.pipe(filter(ready => ready)));

        const configData = (await dbService.getOnce(MainConfig.tableName)) as {
          activeMode?: string;
          activePlateform?: string;
          activeTheme?: string;
          screenLocked?: ScreenStateModel;
        } | null;

        if (configData) {
          const config: ActiveMainConfigModel = {
            activeMode: configData.activeMode as ModeModel,
            activePlateform: configData.activePlateform as PlateformModel,
            activeTheme: configData.activeTheme as ThemeModel,
            screenLocked: configData.screenLocked ?? 'unlocked',
          };
          patchState(store, { config, isHydrated: true });
        } else {
          patchState(store, { isHydrated: true });
        }
      } catch {
        patchState(store, { isHydrated: true });
      }
    },

    async setConfig(config: ActiveMainConfigModel) {
      const configEntity = {
        activeMode: config.activeMode,
        activePlateform: config.activePlateform,
        activeTheme: config.activeTheme,
        screenLocked: config.screenLocked,
      };
      patchState(store, { config });
      try {
        await dbService.addOnceUpdate(MainConfig.tableName, configEntity);
      } catch (error) {
        console.error('setConfig', error);
      }
    },

    getConfig(): ActiveMainConfigModel {
      return store.config();
    },

    isReady() {
      return store.isHydrated();
    },

    async resetConfig() {
      const config: ActiveMainConfigModel = {
        activeMode: store.config().activeMode,
        activePlateform: 'authentification',
        activeTheme: 'magis',
        screenLocked: 'unlocked',
      };

      patchState(store, { config: config });
      try {
        await dbService.addOnceUpdate(MainConfig.tableName, config);
      } catch (error) {
        throw new Error('Error' + error);
      }
    },

    async switchScreenState(state: ScreenStateModel) {
      const config: ActiveMainConfigModel = {
        activeMode: store.config().activeMode,
        activePlateform: store.config().activePlateform,
        activeTheme: store.config().activeTheme,
        screenLocked: state,
      };
      patchState(store, { config: config });
      try {
        await dbService.addOnceUpdate(MainConfig.tableName, config);
      } catch (error) {
        throw new Error('Error' + error);
      }
    },
  })),

  withHooks({
    onInit(store) {
      store.hydrate();
    },
  })
);
